import { MARKETING_COST_DRIVER_UNIT, type MarketingResolvedCost } from '@utazzo/shared';
import { cn, UzTooltip } from '@utazzo/ui';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useDeleteCostEntry, useUpsertCostEntry } from '../use-marketing-reports';

const parseAmount = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed.replace(/,/g, ''));
  return Number.isFinite(value) && value >= 0 ? value : null;
};

const inr = (value: number): string => `₹${Math.round(value).toLocaleString('en-IN')}`;

/** `2026-06` -> "Jun 26". */
const shortMonth = (month: string): string => {
  const [year, index] = month.split('-').map(Number);
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${names[(index ?? 1) - 1] ?? ''} ${String(year).slice(2)}`;
};

/** What the small tag under a figure says about where it came from. */
const provenance = (cost: MarketingResolvedCost): string | null => {
  switch (cost.source) {
    case 'carried':
      return cost.effectiveFrom ? `from ${shortMonth(cost.effectiveFrom)}` : 'carried';
    case 'derived': {
      const unit = cost.units ?? 0;
      return `${unit.toLocaleString('en-IN')} ${cost.rate !== null ? `× ₹${cost.rate}` : 'units'}`;
    }
    case 'override':
      return 'typed over calc';
    default:
      return null;
  }
};

/** Longer hover text for the tag - the formula a derived figure came from. */
const provenanceTitle = (cost: MarketingResolvedCost): string | null => {
  if (cost.mode === 'DERIVED') {
    const unit = cost.driver ? MARKETING_COST_DRIVER_UNIT[cost.driver] : 'units';
    const units = (cost.units ?? 0).toLocaleString('en-IN');
    const base = `${units} ${unit} this month × ₹${cost.rate ?? 0} each`;
    return cost.source === 'override'
      ? `${base}. A typed figure replaces that calculation for this month; clear the cell to go back to it.`
      : `${base}. Type a figure to override it for this month.`;
  }
  if (cost.mode === 'CARRY_FORWARD') {
    return cost.source === 'carried'
      ? `Carried forward from ${cost.effectiveFrom ? shortMonth(cost.effectiveFrom) : 'an earlier month'}. Type a figure here to start a new rate from this month.`
      : 'Rate entered for this month; later months follow it until a new rate is entered.';
  }
  return cost.source === 'missing' ? 'Nothing entered for this month.' : null;
};

/**
 * One cost head's figure for one month - the Excel cell.
 *
 * Reads as a number; click to type, Enter / blur to save, Escape to abandon.
 * The saved amount REPLACES what is stored (for a carried-forward head it starts
 * a new rate from this month; for a calculated head it overrides the
 * calculation). Clearing the cell removes this month's entry, which puts a
 * carried-forward head back on the earlier rate and a calculated head back on
 * its formula.
 *
 * A required head with nothing behind it shows an amber cell rather than a
 * zero - that is the figure locking the month's profit, and it says so.
 */
export const CostCell = ({
  cost,
  month,
  canEdit,
  className,
}: {
  cost: MarketingResolvedCost;
  month: string;
  canEdit: boolean;
  className?: string;
}): React.JSX.Element => {
  const upsert = useUpsertCostEntry();
  const remove = useDeleteCostEntry();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const busy = upsert.isPending || remove.isPending;

  useEffect(() => {
    if (!editing) setDraft(cost.amount === null ? '' : String(cost.amount));
  }, [cost.amount, editing]);

  const commit = async (): Promise<void> => {
    setEditing(false);
    const parsed = parseAmount(draft);
    if (draft.trim() === '') {
      // Cleared: drop this month's entry (no-op when there was none typed).
      if (cost.source === 'entered' || cost.source === 'override') {
        await remove.mutateAsync({ code: cost.code, month });
      }
      return;
    }
    if (parsed === null) {
      toast.error('Enter a number.');
      return;
    }
    if (parsed === cost.amount && cost.source !== 'carried' && cost.source !== 'derived') return;
    await upsert.mutateAsync({ code: cost.code, month, amount: parsed });
  };

  const missing = cost.amount === null;
  const tag = provenance(cost);
  const title = provenanceTitle(cost);

  if (editing) {
    return (
      <td className={cn('border-border border-r border-b p-0', className)}>
        <input
          autoFocus
          inputMode="decimal"
          aria-label={`${cost.name} for ${month}`}
          className="bg-background text-foreground h-full w-full min-w-24 px-2 py-1.5 text-right text-sm tabular-nums outline-none focus:ring-2 focus:ring-(--primary) focus:ring-inset"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              (event.target as HTMLInputElement).blur();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              setDraft(cost.amount === null ? '' : String(cost.amount));
              setEditing(false);
            }
          }}
        />
      </td>
    );
  }

  const body = (
    <button
      type="button"
      disabled={!canEdit || busy}
      onClick={() => canEdit && setEditing(true)}
      className={cn(
        'flex h-full w-full flex-col items-end px-3 py-1 text-right tabular-nums',
        canEdit &&
          'hover:ring-primary/60 hover:bg-primary/8 cursor-text rounded-sm hover:ring-1 hover:ring-inset',
        !canEdit && 'cursor-default',
      )}
      title={canEdit ? 'Click to type a figure' : undefined}
    >
      <span className={cn('text-sm', missing ? 'text-muted-foreground' : 'text-foreground')}>
        {missing ? (cost.isRequired ? 'Required' : '-') : inr(cost.amount!)}
      </span>
      {tag && (
        <span data-export-skip className="text-muted-foreground text-[10px] leading-tight">
          {tag}
        </span>
      )}
    </button>
  );

  return (
    <td
      className={cn(
        'border-border border-r border-b p-0 align-middle',
        // A faint tint on the cells that take typing, so the Spend rows read as
        // the sheet's input area without shouting. Everything below Spend is
        // arithmetic and stays plain, which is the distinction being drawn.
        canEdit && 'bg-primary/4',
        // A required figure that is still blank outranks it: this is the cell
        // holding the month's profit back, so it keeps the amber.
        missing && cost.isRequired && 'bg-warning/15',
        busy && 'opacity-60',
        className,
      )}
    >
      {title ? (
        <UzTooltip label={title} side="top">
          {body}
        </UzTooltip>
      ) : (
        body
      )}
    </td>
  );
};
